'use client';

import { INavListItem } from '@/api/types/public';
import MagicJump from '@/components/MagicJump';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { setSession } from '@/utils/tools';
import { setCampaignEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import { EPlatform } from '@/utils/enums/platform-enum';
import {
  type CSSProperties,
  cloneElement,
  type ReactElement,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { buildSpmPositionCode, useGlobalSpmTerminal } from '@/components/Layout/spmTerminal';
import styles from './index.module.scss';
import {
  resolveRenderedTextNavigationColumnCount,
  resolveTextNavigationColumnCount,
  TEXT_NAVIGATION_COLUMN_GAP,
  TEXT_NAVIGATION_COLUMN_WIDTH,
} from './navigationLayoutPriority';
import { buildSubmenuSpmPosition } from './submenuSpm';

interface TextNavProps {
  item: INavListItem;
  firstLevelIndex: number;
  firstLevelTitle: string; // 一级菜单标题
  secondLevelIndex: number; // 所属的二级菜单索引
  isLast: boolean; // 是否是最后一个
  isDarkMode?: boolean;
  isActive?: boolean;
  /** 扁平坑位序号偏移：本文字区块前面所有区块的条目累计数；用于重建 submenu_item_GG_II 的 II。 */
  spmItemOffset?: number;
}

/**
 * 循环内挂载点 Data SPM DOM 打标：hook 不能在 map 中调用，封为小组件把 data-spm
 * 克隆到唯一真实子元素上（不新增 DOM 层级）；开关关闭或编码表外时原样渲染，零 DOM 差异。
 */
function SpmDomNode({ code, children }: { code: string | undefined; children: ReactElement }) {
  const domSpm = useSpmDomAttr(code);
  return domSpm ? cloneElement(children, domSpm) : children;
}

const TextNav = ({
  item,
  firstLevelIndex,
  firstLevelTitle,
  secondLevelIndex,
  isLast,
  isDarkMode = false,
  isActive = true,
  spmItemOffset = 0,
}: TextNavProps) => {
  const textNavigationRef = useRef<HTMLDivElement>(null);
  const textNavigationItemsRef = useRef<HTMLDivElement>(null);
  const singleColumnHeightRef = useRef(0);
  const [columnCount, setColumnCount] = useState(1);
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  // 文字分类区块只在 PC 下拉面板渲染；全局组件 B 位固定 global_pc。
  const spmPage = useGlobalSpmTerminal(EPlatform.WEB);
  const setIsOpenSideNav = useStore((state) => state.setIsOpenSideNav);
  const isMobileScreen = useStore((state) => state.isMobileScreen);
  const content = item.content;

  useLayoutEffect(() => {
    if (!isActive) return;

    const textNavigation = textNavigationRef.current;
    const textNavigationItems = textNavigationItemsRef.current;
    if (!textNavigation || !textNavigationItems) return;

    const desktopQuery = window.matchMedia('(min-width: 1281px)');
    const updateColumnCount = () => {
      if (!desktopQuery.matches) {
        setColumnCount(1);
        return;
      }

      if (singleColumnHeightRef.current === 0) {
        singleColumnHeightRef.current = textNavigationItems.scrollHeight;
      }

      const title = textNavigation.querySelector<HTMLElement>(`.${styles.secondTitle}`);
      const titleHeight = title?.getBoundingClientRect().height || 0;
      const availableHeight = window.innerHeight * 0.5 - 52 - titleHeight - 16;
      const nextColumnCount = resolveTextNavigationColumnCount({
        contentHeight: singleColumnHeightRef.current,
        availableHeight,
      });
      setColumnCount((currentCount) =>
        currentCount === nextColumnCount ? currentCount : nextColumnCount,
      );
    };

    updateColumnCount();
    window.addEventListener('resize', updateColumnCount);
    return () => window.removeEventListener('resize', updateColumnCount);
  }, [content?.layout_title, content?.list?.length, isActive]);

  useLayoutEffect(() => {
    if (!isActive || columnCount <= 1) return;

    const textNavigationItems = textNavigationItemsRef.current;
    if (!textNavigationItems) return;

    const renderedColumnCount = resolveRenderedTextNavigationColumnCount({
      currentColumnCount: columnCount,
      scrollWidth: textNavigationItems.scrollWidth,
    });
    setColumnCount((currentCount) =>
      currentCount >= renderedColumnCount ? currentCount : renderedColumnCount,
    );
  }, [columnCount, content?.list?.length, isActive]);

  if (!content) return null;

  const handleBeforeJump = (title: string, index: number, targetUrl?: string) => {
    setSession('zl_goodsListEnterPageScene', '全局导航');
    setCampaignEntrancePageScene(isMobileScreen ? '侧边导航' : '顶部导航', targetUrl);
    setIsOpenSideNav(false);
    // 三级条目按下拉面板渲染顺序扁平编号（submenu_item_GG_II，按坑位序号规则出码）。
    const submenuSpmCnt = buildSpmPositionCode(
      spmPage,
      'primary_navigation',
      buildSubmenuSpmPosition(firstLevelIndex, spmItemOffset + index),
    );
    const trackProperties = {
      page_name: cnName,
      url_path: window.location.href,
      tab_name: firstLevelTitle, // 一级菜单标题
      tab_index: firstLevelIndex, // 一级菜单索引
      first_level_category: (item.content as any).layout_title, // 神策埋点定义的二级菜单标题
      second_level_category: title, // 神策埋点定义的三级菜单标题
      first_level_index: secondLevelIndex,
      second_level_index: index,
    };
    trackEvent(
      {
        eventName: isMobileScreen ? 'SideNavBarTab_Click' : 'TopNavigationTab_Click',
        properties: withSpmCnt(trackProperties, submenuSpmCnt),
      },
      [ProviderType.SENSORS],
    );
  };
  const jumpTarget = {
    jump_type: item.content.jump_type || 0,
    Jump_target: item.content.Jump_target || '',
    product_group: {
      id: item.content.product_group?.id,
      path: (item.content as any).product_group?.path,
    },
    sku: {
      shop_sku: (item.content as any).sku?.shop_sku,
    },
  };

  const textNavigationStyle = {
    '--text-navigation-column-count': columnCount,
    '--text-navigation-column-width': `${TEXT_NAVIGATION_COLUMN_WIDTH}px`,
    '--text-navigation-column-gap': `${TEXT_NAVIGATION_COLUMN_GAP}px`,
  } as CSSProperties;

  return (
    <div
      ref={textNavigationRef}
      className={`${styles.textNavPc} ${columnCount > 1 ? styles.textNavPcMulticolumn : ''} ${isDarkMode ? 'md:text-[#ffffff] md:bg-[#131415] ' : 'text-[#282828] md:bg-white'}  pt-[16px] pb-[4px] bg-[#fafafa]   md:pt-0 md:pb-[16px] lg:pb-[4px] lg:pt-[16px] md:flex md:flex-col  md:items-start ${isLast ? 'pb-[24px] lg:pb-[60px]' : ''}`}
      style={textNavigationStyle}
    >
      {/* 二级标题 */}
      <MagicJump
        className={`${styles.secondTitle} ${isDarkMode ? 'text-[#ffffff]' : 'text-[#282828]'} text-[14px] font-bb block py-[7px] px-[28px] md:mb-[12px] lg:mb-0 lg:text-[18px] lg:leading-[22px]  md:border-transparent md:hover:border-primary-black md:inline-block md:p-0`}
        item={jumpTarget}
        title={String((item.content as any).layout_title || '')}
        onBeforeJump={() =>
          setCampaignEntrancePageScene(
            isMobileScreen ? '侧边导航' : '顶部导航',
            jumpTarget.Jump_target,
          )
        }
      >
        {(item.content as any).layout_title}
      </MagicJump>

      {/* 三级列表项 */}
      <div ref={textNavigationItemsRef} className={styles.textNavPcItems}>
        {(item.content as any).list?.map((cItem, idx) => (
          <SpmDomNode
            key={cItem.id}
            code={buildSpmPositionCode(
              spmPage,
              'primary_navigation',
              buildSubmenuSpmPosition(firstLevelIndex, spmItemOffset + idx),
            )}
          >
            <MagicJump
              className={`${styles.textNavItem} cursor-pointer text-[13px] block py-[8px] px-[28px] lg:text-[16px] lg:leading-[20px] md:border-b md:border-transparent md:hover:border-primary-black md:px-0 md:mt-[16px] md:mb-[8px] lg:mb-0 lg:mt-0 md:text-[14px] md:inline-block md:p-0 ${
                idx < (item.content as any).list.length - 1 ? styles.thirdItem : ''
              }`}
              title={String(cItem.title || '')}
              item={{
                jump_type: cItem.jump_type,
                Jump_target: cItem.Jump_target,
                product_group: {
                  id: cItem.product_group?.id,
                  path: cItem.product_group?.path,
                },
                sku: {
                  shop_sku: cItem.sku?.shop_sku,
                },
              }}
              onBeforeJump={() => handleBeforeJump(cItem.title, idx, cItem.Jump_target)}
            >
              {cItem.title}
            </MagicJump>
          </SpmDomNode>
        ))}
      </div>
    </div>
  );
};

export default TextNav;
